import { useCallback, useEffect, useState } from "react";
import { CalendarClock, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

const Pick = ({ value, onChange, items, testid, w = "w-36", ro }) => (
  <Select value={String(value)} onValueChange={(v) => onChange(Number(v))} disabled={ro}>
    <SelectTrigger className={`${w} h-8`} data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{items.map(([v, l]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

const Approved = ({ p, set, ro }) => {
  const toggle = (k) => set("approved")(p.approved.includes(k) ? p.approved.filter((x) => x !== k) : [...p.approved, k]);
  const list = p.available.filter((a) => !(p.all_os && a.kind === "os"));
  return (
    <div>
      <p className="text-xs font-medium text-slate-600 mb-1">{p.all_os ? "Logiciels approuvés" : "Mises à jour approuvées"}</p>
      {!list.length && <p className="text-xs text-slate-500">Aucune mise à jour connue sur ces postes. Lancez « Rechercher les mises à jour » dans l'onglet Parc.</p>}
      <div className="grid sm:grid-cols-2 gap-x-6">
        {list.map((a) => (
          <label key={a.key} className="flex items-center gap-2 py-1 text-sm cursor-pointer" data-testid={`policy-item-${p.group_id}-${a.key}`}>
            <Checkbox checked={p.approved.includes(a.key)} onCheckedChange={() => toggle(a.key)} disabled={ro} />
            <span className="truncate">{a.title}</span><span className="text-[10px] uppercase text-slate-400">{a.kind === "os" ? "système" : "logiciel"}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

const PolicyCard = ({ initial }) => {
  const { user } = useAuth();
  const ro = user?.role !== "admin";
  const [p, setP] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setP((x) => ({ ...x, [k]: v }));
  const save = () => {
    setBusy(true);
    const { enabled, day, hour, all_os, approved } = p;
    api.put(`/rmm/policies/${p.group_id}`, { enabled, day, hour, all_os, approved })
      .then(() => toast.success(`Mises à jour automatiques enregistrées pour ${p.name}`)).catch((e) => toast.error(errMsg(e))).finally(() => setBusy(false));
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid={`policy-${p.group_id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <Switch checked={p.enabled} onCheckedChange={set("enabled")} disabled={ro} data-testid={`policy-switch-${p.group_id}`} />
        <div className="mr-auto"><p className="font-medium">{p.name}</p><p className="text-xs text-slate-500">{p.machines} poste(s){p.last_run ? ` · dernière exécution ${fmtDate(p.last_run)}` : ""}</p></div>
        <span className="text-sm text-slate-600">Chaque</span>
        <Pick value={p.day} onChange={set("day")} items={DAYS.map((d, i) => [i, d])} testid={`policy-day-${p.group_id}`} ro={ro} />
        <span className="text-sm text-slate-600">à</span>
        <Pick value={p.hour} onChange={set("hour")} items={Array.from({ length: 24 }, (_, h) => [h, `${h} h 00`])} testid={`policy-hour-${p.group_id}`} w="w-28" ro={ro} />
        {!ro && <Button size="sm" onClick={save} disabled={busy} data-testid={`policy-save-${p.group_id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Enregistrer</Button>}
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <Checkbox checked={p.all_os} onCheckedChange={(v) => set("all_os")(!!v)} disabled={ro} data-testid={`policy-allos-${p.group_id}`} /> Installer toutes les mises à jour système (Windows / macOS)
      </label>
      <Approved p={p} set={set} ro={ro} />
      {ro && <p className="text-xs text-slate-500">Seul l'administrateur peut modifier ces réglages.</p>}
    </div>
  );
};

export const UpdatePolicies = () => {
  const [list, setList] = useState(null);
  const load = useCallback(() => api.get("/rmm/policies").then(({ data }) => setList(data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!list) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  return (
    <div className="space-y-3" data-testid="update-policies">
      <p className="text-sm text-slate-600 flex items-center gap-2"><CalendarClock className="h-4 w-4 text-blue-600" />
        Approuvez une fois : chaque semaine au créneau choisi, les postes du client sont analysés puis mis à jour. Les postes éteints sont rattrapés dès leur retour en ligne ; l'utilisateur choisit quand redémarrer.</p>
      {!list.length && <p className="text-sm text-slate-500">Aucun client : créez des groupes dans la vue Access.</p>}
      {list.map((p) => <PolicyCard key={p.group_id} initial={p} />)}
    </div>
  );
};
